import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  FileText, Upload, CheckCircle2, XCircle, Eye, Search, RefreshCw, Send, Inbox, Loader2, Filter, X,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  pending_manager_approval: { label: 'Pending Manager Approval', color: 'bg-amber-500/15 text-amber-700 border-amber-500/30' },
  approved_by_manager: { label: 'Approved by Manager', color: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  rejected: { label: 'Rejected', color: 'bg-red-500/15 text-red-700 border-red-500/30' },
  sent_to_finance: { label: 'Sent to Finance', color: 'bg-blue-500/15 text-blue-700 border-blue-500/30' },
  processing: { label: 'Processing', color: 'bg-indigo-500/15 text-indigo-700 border-indigo-500/30' },
  completed: { label: 'Completed', color: 'bg-green-500/15 text-green-700 border-green-500/30' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, color: 'bg-gray-500/15 text-gray-700 border-gray-500/30' };
  return <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${meta.color}`} data-testid={`status-${status}`}>{meta.label}</span>;
};

export const ApprovalsModule = ({ api, user }) => {
  const role = user?.role;
  const isManager = role === 'manager';
  const isFinance = role === 'finance' || role === 'finance_audit';
  const isAdmin = role === 'admin' || role === 'hub_manager';

  const [config, setConfig] = useState({ departments: [], document_types: [], managers: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advFilters, setAdvFilters] = useState({ department: 'all', document_type: 'all', from_date: '', to_date: '' });

  const [form, setForm] = useState({ department: '', document_type: '', remarks: '', manager_id: '' });
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      if (advFilters.department !== 'all') params.department = advFilters.department;
      if (advFilters.document_type !== 'all') params.document_type = advFilters.document_type;
      if (advFilters.from_date) params.from_date = advFilters.from_date;
      if (advFilters.to_date) params.to_date = advFilters.to_date;
      const [cfg, list] = await Promise.all([
        api.get('/approvals/config').catch(() => ({ data: { departments: [], document_types: [], managers: [] } })),
        api.get('/approvals', { params }).catch(() => ({ data: [] })),
      ]);
      setConfig(cfg.data);
      setItems(list.data);
    } finally {
      setLoading(false);
    }
  };

  // initial + when filters change
  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, advFilters]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(it =>
      (it.request_id || '').toLowerCase().includes(q) ||
      (it.requester_name || '').toLowerCase().includes(q) ||
      (it.requester_email || '').toLowerCase().includes(q) ||
      (it.department || '').toLowerCase().includes(q) ||
      (it.document_type || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const resetForm = () => {
    setForm({ department: '', document_type: '', remarks: '', manager_id: '' });
    setFile(null);
  };

  const onFilePick = (f) => {
    if (!f) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(f.type)) { toast.error('Only PDF, JPEG, JPG, PNG allowed'); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error('File exceeds 5 MB'); return; }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    onFilePick(e.dataTransfer.files?.[0]);
  };

  const onSubmit = async () => {
    if (!form.department || !form.document_type || !form.manager_id) {
      toast.error('Please complete department, document type and manager');
      return;
    }
    if ((form.remarks || '').trim().length < 10) {
      toast.error('Remarks must be at least 10 characters');
      return;
    }
    if (!file) { toast.error('Please upload a document'); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('department', form.department);
      fd.append('document_type', form.document_type);
      fd.append('remarks', form.remarks.trim());
      fd.append('manager_id', form.manager_id);
      fd.append('file', file);
      const res = await api.post('/approvals', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Submitted as ${res.data.approval.request_id}`);
      setShowForm(false);
      resetForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const onApprove = async (item, remarks = '') => {
    try {
      await api.post(`/approvals/${item.id}/approve`, { remarks });
      toast.success(`Approved & forwarded to Finance`);
      fetchAll();
      setShowDetail(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const onReject = async () => {
    if ((rejectRemarks || '').trim().length < 10) {
      toast.error('Rejection remarks must be at least 10 characters');
      return;
    }
    try {
      await api.post(`/approvals/${rejecting.id}/reject`, { remarks: rejectRemarks.trim() });
      toast.success('Request rejected');
      setRejecting(null);
      setRejectRemarks('');
      setShowDetail(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    }
  };

  const onFinanceUpdate = async (item, status) => {
    try {
      await api.post(`/approvals/${item.id}/finance-update`, { status });
      toast.success(`Marked as ${status}`);
      fetchAll();
      setShowDetail(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const downloadAttachment = (item) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/approvals/${item.id}/attachment`;
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const objUrl = window.URL.createObjectURL(blob);
        window.open(objUrl, '_blank', 'noopener');
        setTimeout(() => window.URL.revokeObjectURL(objUrl), 60000);
      }).catch(() => toast.error('Failed to load attachment'));
  };

  const pendingForMe = filtered.filter(it => it.status === 'pending_manager_approval' && isManager);
  const financeQueue = filtered.filter(it => ['sent_to_finance', 'processing'].includes(it.status) && isFinance);
  // For managers: everything routed to them (incl. already actioned), recent first.
  // For finance: everything routed to them (incl. completed).
  // For requesters / admin: every row they're allowed to see (already filtered backend-side).
  const routedToMe = isManager
    ? filtered.filter(it => it.manager_id === user?.id && it.status !== 'pending_manager_approval')
    : isFinance
    ? filtered.filter(it => (it.finance_user_ids || []).includes(user?.id) && !['sent_to_finance', 'processing'].includes(it.status))
    : [];
  const myRequests = isManager || isFinance ? filtered.filter(it => it.requester_id === user?.id) : filtered;

  return (
    <div className="space-y-6" data-testid="approvals-module">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" /> Approvals
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {isManager ? 'Approve or reject requests routed to you' :
               isFinance ? 'Process approved requests' :
               isAdmin ? 'All approval requests across the company' :
               'Submit and track your approval requests'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="approvals-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setShowForm(true)} data-testid="approvals-new-btn">
              <Send className="w-4 h-4 mr-1" /> New Request
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by request ID, name, email, department..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="approvals-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="sm:w-56" data-testid="approvals-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showAdvanced ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowAdvanced(s => !s)}
              data-testid="approvals-toggle-advanced"
            >
              <Filter className="w-4 h-4 mr-1" /> Filters
              {(advFilters.department !== 'all' || advFilters.document_type !== 'all' || advFilters.from_date || advFilters.to_date) && (
                <span className="ml-1 inline-flex w-2 h-2 rounded-full bg-primary" />
              )}
            </Button>
          </div>

          {showAdvanced && (
            <div className="mb-4 p-3 rounded-lg border bg-muted/30">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Department</label>
                  <Select value={advFilters.department} onValueChange={(v) => setAdvFilters({ ...advFilters, department: v })}>
                    <SelectTrigger className="mt-1" data-testid="filter-department"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {config.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Document Type</label>
                  <Select value={advFilters.document_type} onValueChange={(v) => setAdvFilters({ ...advFilters, document_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="filter-doctype"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {config.document_types.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">From Date</label>
                  <Input
                    type="date"
                    value={advFilters.from_date}
                    onChange={(e) => setAdvFilters({ ...advFilters, from_date: e.target.value })}
                    className="mt-1"
                    data-testid="filter-from-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">To Date</label>
                  <Input
                    type="date"
                    value={advFilters.to_date}
                    onChange={(e) => setAdvFilters({ ...advFilters, to_date: e.target.value })}
                    className="mt-1"
                    data-testid="filter-to-date"
                  />
                </div>
              </div>
              {(advFilters.department !== 'all' || advFilters.document_type !== 'all' || advFilters.from_date || advFilters.to_date) && (
                <button
                  className="mt-3 text-xs text-muted-foreground hover:text-foreground inline-flex items-center"
                  onClick={() => setAdvFilters({ department: 'all', document_type: 'all', from_date: '', to_date: '' })}
                  data-testid="filter-clear"
                >
                  <X className="w-3 h-3 mr-1" /> Clear filters
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-center py-10 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading…
            </div>
          ) : (
            <>
              {isManager && pendingForMe.length > 0 && (
                <Section title={`⚡ Pending Your Action (${pendingForMe.length})`} items={pendingForMe} onView={setShowDetail} role={role} />
              )}
              {isFinance && financeQueue.length > 0 && (
                <Section title={`📥 In Your Finance Queue (${financeQueue.length})`} items={financeQueue} onView={setShowDetail} role={role} />
              )}
              {isManager && routedToMe.length > 0 && (
                <Section title={`✅ Already Actioned (${routedToMe.length})`} items={routedToMe} onView={setShowDetail} role={role} />
              )}
              {isFinance && routedToMe.length > 0 && (
                <Section title={`✅ Processed by You (${routedToMe.length})`} items={routedToMe} onView={setShowDetail} role={role} />
              )}
              {(myRequests.length > 0 || (!isManager && !isFinance)) && (
                <Section
                  title={isManager || isFinance ? 'My Own Requests' : isAdmin ? 'All Requests' : 'My Requests'}
                  items={myRequests}
                  onView={setShowDetail}
                  role={role}
                />
              )}
              {filtered.length === 0 && (
                <div className="text-center py-10 text-muted-foreground">
                  <Inbox className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No approval requests yet.</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* New Request Modal */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="approvals-new-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">New Approval Request</DialogTitle>
            <DialogDescription>Fill in the details below. Document max 5 MB (PDF, JPG, PNG).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Department *</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger data-testid="form-dept"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    {config.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Document Type *</Label>
                <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                  <SelectTrigger data-testid="form-doctype"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {config.document_types.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Document * (max 5 MB)</Label>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                data-testid="form-file-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => onFilePick(e.target.files?.[0])}
                  data-testid="form-file-input"
                />
                {file ? (
                  <div className="text-sm">
                    <p className="font-semibold">{file.name}</p>
                    <p className="text-muted-foreground text-xs mt-1">{(file.size / 1024).toFixed(1)} KB · {file.type}</p>
                    <button className="text-xs text-red-500 mt-2 underline" onClick={(e) => { e.stopPropagation(); setFile(null); }}>Remove</button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-7 h-7 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm">Drag &amp; drop a file, or click to browse</p>
                    <p className="text-xs text-muted-foreground mt-1">PDF, JPEG, JPG, PNG · 5 MB max</p>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Remarks * (min 10 characters)</Label>
              <Textarea
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                placeholder="Provide context / purpose of approval..."
                rows={3}
                data-testid="form-remarks"
              />
              <p className="text-xs text-muted-foreground">{(form.remarks || '').trim().length} / 10 characters</p>
            </div>

            <div className="space-y-1.5">
              <Label>Approval Authority (Manager) *</Label>
              <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
                <SelectTrigger data-testid="form-manager"><SelectValue placeholder="Select manager" /></SelectTrigger>
                <SelectContent>
                  {config.managers.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
            <Button onClick={onSubmit} disabled={submitting} data-testid="form-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      {showDetail && (
        <ApprovalDetailModal
          open={!!showDetail}
          onClose={() => setShowDetail(null)}
          item={showDetail}
          role={role}
          userId={user?.id}
          onApprove={onApprove}
          onAskReject={(it) => { setRejecting(it); setRejectRemarks(''); }}
          onFinanceUpdate={onFinanceUpdate}
          onDownload={downloadAttachment}
        />
      )}

      {/* Reject Modal */}
      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) { setRejecting(null); setRejectRemarks(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Request</DialogTitle>
            <DialogDescription>Please provide a reason. The requester will be notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Reason for rejection * (min 10 characters)</Label>
            <Textarea value={rejectRemarks} onChange={(e) => setRejectRemarks(e.target.value)} rows={4} data-testid="reject-remarks" />
            <p className="text-xs text-muted-foreground">{(rejectRemarks || '').trim().length} / 10 characters</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setRejectRemarks(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={onReject} data-testid="reject-confirm-btn">
              <XCircle className="w-4 h-4 mr-1" /> Confirm Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Section = ({ title, items, onView, role }) => (
  <div className="mb-6">
    <h3 className="font-semibold text-sm mb-2">{title}</h3>
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr className="text-left text-xs uppercase text-muted-foreground">
            <th className="px-3 py-2">Request ID</th>
            <th className="px-3 py-2">Raised By</th>
            <th className="px-3 py-2">Department</th>
            <th className="px-3 py-2">Document</th>
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t hover:bg-muted/20" data-testid={`approval-row-${it.request_id}`}>
              <td className="px-3 py-2 font-mono text-xs">{it.request_id}</td>
              <td className="px-3 py-2">{it.requester_name}<div className="text-xs text-muted-foreground">{it.requester_email}</div></td>
              <td className="px-3 py-2">{it.department}</td>
              <td className="px-3 py-2">{it.document_type}</td>
              <td className="px-3 py-2 text-xs">{new Date(it.created_at).toLocaleString()}</td>
              <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
              <td className="px-3 py-2">
                <Button variant="outline" size="sm" onClick={() => onView(it)} data-testid={`view-${it.request_id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ApprovalDetailModal = ({ open, onClose, item, role, userId, onApprove, onAskReject, onFinanceUpdate, onDownload }) => {
  const isMyApproval = role === 'manager' && item.manager_id === userId && item.status === 'pending_manager_approval';
  const isFinanceAction = (role === 'finance' || role === 'finance_audit') && (item.finance_user_ids || []).includes(userId);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="approval-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-mono">{item.request_id}</DialogTitle>
          <DialogDescription>
            <StatusBadge status={item.status} />
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <DetailRow label="Raised By" value={`${item.requester_name} · ${item.requester_email}`} />
          <DetailRow label="Department" value={item.department} />
          <DetailRow label="Document Type" value={item.document_type} />
          <DetailRow label="Assigned Manager" value={`${item.manager_name} · ${item.manager_email}`} />
          <DetailRow label="Submitted At" value={new Date(item.created_at).toLocaleString()} />
          <DetailRow label="Remarks" value={item.remarks} />
          {item.manager_remarks && <DetailRow label="Manager Remarks" value={item.manager_remarks} highlight />}
          {item.finance_user_names && item.finance_user_names.length > 0 && (
            <DetailRow label="Finance Recipients" value={item.finance_user_names.join(', ')} />
          )}
          <div className="border-t pt-3">
            <p className="text-xs text-muted-foreground mb-2">Attachment</p>
            <Button variant="outline" size="sm" onClick={() => onDownload(item)} data-testid="detail-download-btn">
              <FileText className="w-4 h-4 mr-1" /> {item.attachment_filename} ({Math.round((item.attachment_size || 0) / 1024)} KB)
            </Button>
          </div>
          {item.audit_log && item.audit_log.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-xs text-muted-foreground mb-2">Timeline</p>
              <ul className="space-y-1.5 text-xs">
                {item.audit_log.map((e, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-muted-foreground">{new Date(e.at).toLocaleString()}</span>
                    <span className="font-medium">{e.action.replace(/_/g, ' ')}</span>
                    <span>by {e.by_name || '—'}</span>
                    {e.details && <span className="text-muted-foreground">— {e.details}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          {isMyApproval && (
            <>
              <Button variant="destructive" onClick={() => onAskReject(item)} data-testid="detail-reject-btn">
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
              <Button onClick={() => onApprove(item, '')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="detail-approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
              </Button>
            </>
          )}
          {isFinanceAction && item.status === 'sent_to_finance' && (
            <Button onClick={() => onFinanceUpdate(item, 'processing')} data-testid="detail-processing-btn">Mark Processing</Button>
          )}
          {isFinanceAction && (item.status === 'processing' || item.status === 'sent_to_finance') && (
            <Button onClick={() => onFinanceUpdate(item, 'completed')} className="bg-green-600 hover:bg-green-700" data-testid="detail-completed-btn">Mark Completed</Button>
          )}
          <Button variant="outline" onClick={() => onClose(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DetailRow = ({ label, value, highlight }) => (
  <div className="grid grid-cols-3 gap-3 items-start">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`col-span-2 text-sm ${highlight ? 'text-amber-700 font-semibold' : ''}`}>{value}</span>
  </div>
);

export default ApprovalsModule;
